import type {
  CellFocusedEvent,
  ColDef,
  ColumnEvent,
  ColumnState,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  MenuItemDef,
  RowDragEndEvent,
  RowDragEvent,
} from 'ag-grid-community'
import { BulkSheetView } from '../../../../containers/BulkSheetView'
import {
  defaultColDef as gridDefaultColDef,
  frameworkComponents,
} from '../../../../containers/commons/AgGrid'
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { intl } from '../../../../../i18n'
import { isAiAvailable } from '../../../../../lib/functions/ai'
import { VersionReportStatus } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import { EvaluateResult } from '../../../../../domain/entity/version-report/evaluation'
import { TeamEntity } from '../../../../../domain/entity/TeamEntity'
import styled from 'styled-components'
import { colorPalette } from '../../../../style/colorPalette'
import {
  CommentExpansionContext,
  CommentExpansionState,
} from '../cell/CommentCellRenderer'
import { columnEventValidSources } from '../../hooks/pageDisplayState'

export type ProgressReportTableContent = {
  uuid: string
  name: string
  status: VersionReportStatus
  comment: string
  scheduledProgressRate: string
  progressRate: string
  total: string
  planned: string
  completed: string
  delay: string
  remaining: string
  wbsItemStatus: string
  team?: TeamEntity
  progressRateDiff: string
  scheduledStartDate?: string
  scheduledEndDate?: string
  maxChildScheduledEndDate?: string
  preceding: string
  unplanned: string
}
export type Value = {
  workload: number
  count: number
}

type ProgressReportTableProps = {
  editable: boolean
  columns: ColDef[]
  contents: ProgressReportTableContent[]
  context?: Record<string, unknown>
  onGridReady?: (event: GridReadyEvent) => void
  updateColumnStateConfig?: (state: ColumnState[]) => void
  onRowDragEnter?: (event: RowDragEvent) => void
  onRowDragMove?: (event: RowDragEvent) => void
  onRowDragLeave?: (event: RowDragEvent) => void
  onRowDragEnd?: (event: RowDragEndEvent) => void
  rowHeight?: number
  columnState?: ColumnState[]
  // Redis 復元時のみ増えるトークン。値が変わったときだけ columnState を再適用し、
  // ユーザーのリサイズ操作中 (columnState の往復) には再適用しない。
  columnStateApplyToken?: number
  onEvaluateRow?: (targetUuid: string) => Promise<EvaluateResult | undefined>
}
export const ProgressReportTable = forwardRef<
  HTMLDivElement,
  ProgressReportTableProps
>(
  (
    {
      contents,
      editable,
      context,
      onGridReady,
      updateColumnStateConfig,
      onRowDragEnter,
      onRowDragMove,
      onRowDragLeave,
      onRowDragEnd,
      rowHeight,
      columnState,
      columnStateApplyToken,
      columns,
      onEvaluateRow,
    }: ProgressReportTableProps,
    ref
  ) => {
    const [gridInitialized, setGridInitialized] = useState(false)
    const [expandedKey, setExpandedKey] = useState<string | null>(null)
    const gridApiRef = useRef<GridApi | null>(null)

    const expansionContextValue = useMemo<CommentExpansionState>(
      () => ({ expandedKey, setExpandedKey }),
      [expandedKey]
    )

    const defaultColDef = useMemo<ColDef>(
      () => gridDefaultColDef({ editable: false, sortable: true }),
      []
    )

    const gridOptions = useMemo(
      () => ({
        columnDefs: columns,
        defaultColDef,
        defaultColGroupDef: { suppressHeaderContextMenu: true },
        maintainColumnOrder: true,
        tooltipShowDelay: 100,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )

    const onColumnChanged = useCallback(
      (e: ColumnEvent) => {
        if (!e.api) return
        if (!e.source || !columnEventValidSources.has(e.source)) return
        // ドラッグ確定時 (finished:true) のみ保存する。
        if ('finished' in e && e.finished === false) return
        const state = e.api.getColumnState()
        updateColumnStateConfig?.(state)
      },
      [updateColumnStateConfig]
    )

    const applyStoredColumnState = useCallback(() => {
      if (gridApiRef.current && columnState && columnState.length > 0) {
        gridApiRef.current.applyColumnState({
          state: columnState,
          applyOrder: true,
        })
        gridApiRef.current.moveColumns(['drag'], 0)
      }
    }, [columnState])
    const applyStoredColumnStateRef = useRef(applyStoredColumnState)
    useEffect(() => {
      applyStoredColumnStateRef.current = applyStoredColumnState
    }, [applyStoredColumnState])

    const handleGridReady = useCallback(
      (e: GridReadyEvent) => {
        gridApiRef.current = e.api
        applyStoredColumnStateRef.current()
        onGridReady?.(e)
        // for avoiding flickering, set a delay before showing the grid
        setTimeout(() => setGridInitialized(true), 200)
      },
      [onGridReady]
    )

    const onEvaluateRowRef = useRef(onEvaluateRow)
    useEffect(() => {
      onEvaluateRowRef.current = onEvaluateRow
    }, [onEvaluateRow])

    const getContextMenuItems = useCallback(
      (
        params: GetContextMenuItemsParams<ProgressReportTableContent>
      ): MenuItemDef[] => {
        if (!editable || !isAiAvailable() || !onEvaluateRowRef.current) {
          return []
        }
        const uuid = params.node?.data?.uuid
        if (!uuid) {
          return []
        }
        return [
          {
            name: intl.formatMessage({
              id: 'versionReport.evaluate.row',
            }),
            action: async () => {
              const result = await onEvaluateRowRef.current?.(uuid)
              if (!result) return
              const node = gridApiRef.current?.getRowNode(result.uuid)
              if (!node) return
              node.setDataValue('status', result.status)
              node.setDataValue('comment', result.comment)
            },
          },
        ]
      },
      [editable]
    )

    const onCellFocused = useCallback((event: CellFocusedEvent) => {
      const { column } = event
      if (!column) return
      const colId = typeof column === 'string' ? column : column.getColId()
      // 定性コメント展開ボタン操作時に列が勝手に移動するのを防ぐ
      if (colId === 'comment') return
      event.api.ensureColumnVisible(column)
    }, [])

    // editable 変化で列定義 (drag 列表示・rowDrag 等) が変わるため api で反映する。
    // columnDefs 入れ替えで列幅がリセットされるので、現在の列状態を保存→復元する。
    useEffect(() => {
      const api = gridApiRef.current
      if (!api) return
      const currentState = api.getColumnState()
      api.setGridOption('columnDefs', columns)
      api.applyColumnState({ state: currentState, applyOrder: true })
      api.moveColumns(['drag'], 0)
      api.refreshHeader()
    }, [columns])

    // Redis から復元した時 (token 変化時) のみ列状態を再適用する。
    useEffect(() => {
      applyStoredColumnStateRef.current()
    }, [columnStateApplyToken])

    useEffect(() => {
      gridApiRef.current?.refreshCells({ columns: ['rowNumber'], force: true })
    }, [contents])

    return (
      <CommentExpansionContext.Provider value={expansionContextValue}>
        <HiddenUntilInitialized isVisible={gridInitialized}>
          <BulkSheetView
            ref={ref}
            gridOptions={gridOptions}
            rowData={contents}
            context={context}
            rowHeight={rowHeight}
            components={frameworkComponents}
            onGridReady={handleGridReady}
            suppressContextMenu={!onEvaluateRow}
            getContextMenuItems={getContextMenuItems}
            suppressMovableColumns={!editable}
            suppressMoveWhenRowDragging={!editable}
            suppressDragLeaveHidesColumns={!editable}
            rowDragManaged={false}
            suppressRowDrag={!editable}
            onRowDragEnter={onRowDragEnter}
            onRowDragMove={onRowDragMove}
            onRowDragLeave={onRowDragLeave}
            onRowDragEnd={onRowDragEnd}
            onColumnVisible={onColumnChanged}
            onColumnResized={onColumnChanged}
            onColumnMoved={onColumnChanged}
            enterNavigatesVertically={true}
            enterNavigatesVerticallyAfterEdit={true}
            onCellFocused={onCellFocused}
            processUnpinnedColumns={() => []}
            {...(!editable ? { sideBar: undefined } : {})}
          />
        </HiddenUntilInitialized>
      </CommentExpansionContext.Provider>
    )
  }
)

const HiddenUntilInitialized = styled('div')<{ isVisible: boolean }>(
  ({ isVisible }) => ({
    visibility: isVisible ? 'visible' : 'hidden',
    width: '100%',
    height: '100%',
    '& .ag-theme-balham': {
      '--ag-cell-horizontal-border': `1px dashed ${colorPalette.monotone[2]}`,
    },

    '& .ag-header-cell.multiline-header .ag-header-cell-text': {
      whiteSpace: 'pre !important',
      lineHeight: 1.3,
      textAlign: 'left',
    },

    '& .ag-header-cell .ag-header-cell-label': {
      justifyContent: 'flex-start',
    },
    '& .ag-cell.comment-cell-expandable': {
      overflow: 'visible !important',
    },
    '& .ag-row.has-expanded-comment-cell': {
      overflow: 'visible !important',
      zIndex: 10,
      backgroundColor: `${colorPalette.skyBlue[1]} !important`,
    },
    '& .ag-row.has-expanded-comment-cell .ag-cell': {
      backgroundColor: `${colorPalette.skyBlue[1]} !important`,
    },
  })
)
